import type { ITaskCreatePayload } from "@/src/api/tasksPageRequests";
import { ADMISSION_OFFICE_ASSIGNEE_LABEL } from "@/src/constants/documentPreparationPopUpConsts";
import { AOP_APPROVE_TASK_FIELD_MAP } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { getCurrentUserId } from "@/src/utils/tasks/buildTaskCreatePayload";

const readFormString = (formValues: Record<string, unknown>, field: string): string => {
  const value = formValues[field];
  if (value == null) return "";
  return String(value).trim();
};

const resolveSelectedCourse = (formValues: Record<string, unknown>): string => {
  const selectedCourse = readFormString(formValues, "selectedCourse");
  if (selectedCourse) return selectedCourse;
  return readFormString(formValues, "course");
};

const resolveTaskTypeLabel = (formValues: Record<string, unknown>): string => {
  const explicitType = readFormString(formValues, "type");
  if (explicitType) return explicitType;
  return formValues.addInvoice ? "Admission/Invoice Letter" : "Admission Letter";
};

const resolveDeadlineDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split("T")[0];
};

const resolveTuitionFeeFlag = (formValues: Record<string, unknown>): boolean => {
  const value = formValues.tuitionFeeFlag;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    return trimmed === "true" || trimmed === "1";
  }
  return false;
};

/**
 * Builds POST /tasks/create body from Admissions Office form values
 * (Student Id prefill → Generate doc path).
 */
export const buildTaskCreatePayloadFromAOPForm = (
  formValues: Record<string, unknown>,
  applicantId: number,
): ITaskCreatePayload => {
  const currentUserId = getCurrentUserId();
  const mappedFields: Partial<ITaskCreatePayload> = {};

  AOP_APPROVE_TASK_FIELD_MAP.forEach(({ formField, apiKey, transform }) => {
    if (apiKey === "type" || apiKey === "whoose" || apiKey === "selected_course") return;
    const raw = formValues[formField];
    const value = transform ? transform(raw) : raw == null ? "" : String(raw);
    const trimmed = value.trim();
    if (trimmed) {
      (mappedFields as Record<string, string>)[apiKey] = trimmed;
    }
  });

  const whoose = readFormString(formValues, "whose_doc") || ADMISSION_OFFICE_ASSIGNEE_LABEL;
  const selectedCourse = resolveSelectedCourse(formValues);

  return {
    applicant_id: applicantId,
    assignee_id: currentUserId,
    executor_id: currentUserId,
    whoose,
    type: resolveTaskTypeLabel(formValues),
    intake: mappedFields.intake ?? "",
    admission_semester: mappedFields.admission_semester ?? "",
    form_of_education: mappedFields.form_of_education ?? "",
    selected_course: selectedCourse,
    category: mappedFields.category ?? "",
    medium_of_instruction: mappedFields.medium_of_instruction ?? "",
    duration: mappedFields.duration ?? "",
    transfer_fee: mappedFields.transfer_fee ?? "",
    priority: readFormString(formValues, "priority") || "normal",
    note: readFormString(formValues, "note"),
    deadline_date: readFormString(formValues, "deadline_date") || resolveDeadlineDate(),
    attached_file: null,
    tuition_fee: mappedFields.tuition_fee ?? "",
    tuition_fee_flag: resolveTuitionFeeFlag(formValues),
    medical_insurance: mappedFields.medical_insurance ?? "",
    residence_permit: mappedFields.residence_permit ?? "",
    other_expenses: mappedFields.other_expenses ?? "",
    acommodation: mappedFields.acommodation ?? "",
    admission_fee: mappedFields.admission_fee ?? "",
    complete_one_year: mappedFields.complete_one_year ?? "",
  };
};

export const parseCreatedTaskId = (response: unknown): string | null => {
  if (response == null) return null;

  if (typeof response === "string" || typeof response === "number") {
    const value = String(response).trim();
    return value || null;
  }

  if (typeof response !== "object") return null;

  const record = response as Record<string, unknown>;
  for (const key of ["id", "task_id", "taskId"] as const) {
    const value = record[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }

  if (record.data != null) return parseCreatedTaskId(record.data);
  if (record.task != null) return parseCreatedTaskId(record.task);

  return null;
};
